/**
 * Pick the Sizing — multiple choice over sheet 1's sizing rules.
 *
 * Hand-authored questions pulled directly from sheet 1 § "Sizing
 * Preflop." Each question is a specific factual question with
 * unambiguous canonical answer — preflop sizing is one of the few
 * places where the sheets give precise numbers, so the quiz can be
 * crisp.
 *
 * Curated set rather than procedural because sizing rules don't
 * compose well — there's no formula to vary. ~12 questions cycle.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export interface PickSizingQuestion {
  prompt: string;
  options: string[];
  correctIndex: number;
  rationale: string;
}

export const PICK_SIZING_BANK: PickSizingQuestion[] = [
  {
    prompt: "Standard online open-raise sizing from any position?",
    options: ["1bb (min-raise)", "2–2.5×", "3–5×", "6×+"],
    correctIndex: 1,
    rationale: "Online play uses 2–2.5× opens. Smaller is more capital-efficient and players defend tighter against larger raises online.",
  },
  {
    prompt: "Standard live open-raise sizing (no limpers, no antes)?",
    options: ["1bb (min-raise)", "2–2.5×", "3–5×", "8×+"],
    correctIndex: 2,
    rationale: "Live tables call too much; you need 3–5× to thin the field and price out weak draws. Online's 2–2.5× gets called 8-handed by everything.",
  },
  {
    prompt: "Open-raise sizing adjustment for each limper ahead?",
    options: ["No change", "+1bb per limper", "Double per limper", "Always cap at 4×"],
    correctIndex: 1,
    rationale: "Each limper adds dead money: bump your iso-raise by ~1bb per limper to maintain fold equity and pot leverage.",
  },
  {
    prompt: "Standard 3-bet sizing IN POSITION, as a multiple of the open?",
    options: ["2× the open", "3× the open (sometimes 2.7×)", "4× the open", "5× the open"],
    correctIndex: 1,
    rationale: "IP 3-bets ~3× the open. Position lets you go smaller: you'll realize more equity postflop and don't need to deny villain's equity as aggressively.",
  },
  {
    prompt: "Standard 3-bet sizing OUT OF POSITION, as a multiple of the open?",
    options: ["2× the open", "3× the open", "4× the open", "All-in"],
    correctIndex: 2,
    rationale: "OOP 3-bets ~4× the open. You need the bigger size to deny equity, discourage flats, and build a more workable pot since you'll be playing without position postflop.",
  },
  {
    prompt: "Standard 4-bet sizing, as a multiple of the 3-bet?",
    options: ["1.5× the 3-bet", "2.2–2.5× the 3-bet", "3× the 3-bet", "Always all-in"],
    correctIndex: 1,
    rationale: "4-bets ~2.2–2.5× the 3-bet. Smaller than instinct suggests: the threat of the 4-bet does most of the work; size doesn't need to do all of it.",
  },
  {
    prompt: "Cold-calling a raise: what size do you put in?",
    options: ["Match the raise", "Half the raise", "Always 3-bet instead", "Min-raise"],
    correctIndex: 0,
    rationale: "Calling means matching the raise exactly. There's no sizing decision on a flat; the only sizing decisions are open / raise / 4-bet.",
  },
  {
    prompt: "Bigger raises with stronger hands, smaller with weaker hands: leak or fine?",
    options: ["Fine: extracts more value", "Leak: telegraphs hand strength", "Depends on the table", "Only a leak against regs"],
    correctIndex: 1,
    rationale: "Sizing tells are a major leak. Standardize one size per spot regardless of hand strength: if you only raise 4× with AA and 2.5× with everything else, observant villains exploit it for free.",
  },
  {
    prompt: "Antes are introduced. Your opening range should…",
    options: ["Tighten: more action coming", "Stay the same", "Widen: more dead money", "Only open premiums"],
    correctIndex: 2,
    rationale: "Antes pump dead money into every pot. Wider opens are profitable because you're risking the same to win more.",
  },
  {
    prompt: "Open-limping behind another limper preflop?",
    options: ["Fine if hand has potential", "Leak: either raise or fold", "Mandatory with small pairs", "Standard against a tight table"],
    correctIndex: 1,
    rationale: "Open-limping is almost always a leak. Either raise (for value or to isolate) or fold. Limping invites a multi-way flop where you bleed money OOP with marginal hands.",
  },
  {
    prompt: "Effective stack 50bb (short cash / shallow). Your range should…",
    options: ["Stay the same", "Favor suited connectors + small pairs", "Favor high cards + pairs", "Always open-shove"],
    correctIndex: 2,
    rationale: "Short stacks reward HIGH-CARD STRENGTH and pairs. Suited connectors and small pairs lose value at 50bb because you don't have the implied odds to chase. Drop them; lean on Ax / Kx / Qx / pairs.",
  },
  {
    prompt: "Deep stacks (200bb+). What hand types gain the most value?",
    options: ["Offsuit broadways", "High pairs only", "Suited and connected hands", "Min-raise hands"],
    correctIndex: 2,
    rationale: "Deep stacks reward suited and connected hands: they make the biggest hands (flushes, straights, sets) which can win huge pots. Offsuit broadways lose value because they make second-best hands.",
  },
];

export function generateQuestion(
  rand: () => number = Math.random,
): PickSizingQuestion {
  return pickFrom(PICK_SIZING_BANK, rand);
}

export const _internals = { PICK_SIZING_BANK };
